import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, Edit, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `/api`;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceStatusMap, setServiceStatusMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const [formData, setFormData] = useState({
    client_id: '',
    project_name: '',
    service_type: '',
    status: '',
    deadline: '',
    payment_status: 'Pending',
    value: '',
    assigned_person: '',
    assigned_role: '',
    internal_notes: '',
  });

  /* ================= FETCH ================= */

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [projectsRes, clientsRes, typesRes] =
        await Promise.all([
          axios.get(`${API}/projects`),
          axios.get(`${API}/clients`),
          axios.get(`${API}/master/service-types`)
        ]);

      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setServiceTypes(typesRes.data);
      
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
const [roles, setRoles] = useState([]);
axios.get(`${API}/master/roles`)

  /* ================= FILTER ================= */

  const filteredProjects = projects.filter(p =>
    p.project_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      value: parseFloat(formData.value)
    };

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, payload);
        toast.success('Project updated');
      } else {
        await axios.post(`${API}/projects`, payload);
        toast.success('Project created');
      }

      handleCancel();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save project');
    }
  };

  /* ================= DELETE ================= */

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    await axios.delete(`${API}/projects/${id}`);
    fetchAll();
  };

  /* ================= EDIT ================= */

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      ...project,
      deadline: project.deadline?.split('T')[0] || ''
    });
    setClientSearch('');
    setIsDialogOpen(true);
  };

  /* ================= CANCEL ================= */

  const handleCancel = () => {
    setEditingProject(null);
    setFormData({
      client_id: '',
      project_name: '',
      service_type: '',
      status: '',
      deadline: '',
      payment_status: 'Pending',
      value: '',
      assigned_person: '',
      assigned_role: '',
      internal_notes: '',
    });
    setClientSearch('');
    setIsDialogOpen(false);
  };

  /* ================= UTIL ================= */

  const isOverdue = (deadline, status) => {
    if (!deadline) return false;
    if (status === 'Delivered' || status === 'Closed') return false;
    return new Date(deadline) < new Date();
  };

  const getClientName = (id) => {
    return clients.find(c => c.id === id)?.client_name || 'Unknown';
  };

  if (loading) {
    return <div className="text-center py-12">Loading projects...</div>;
  }

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div className="relative w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-subtext" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Project
        </Button>
      </div>

      {/* TABLE */}
      <div className="bg-app-card border border-app-border rounded-lg overflow-hidden">
        <div className="divide-y divide-app-border">
          {filteredProjects.map(project => {
            const overdue = isOverdue(project.deadline, project.status);

            return (
              <div
                key={project.id}
                className={`grid grid-cols-8 gap-4 px-6 py-4 items-center hover:bg-app-muted ${overdue ? 'bg-red-500/5' : ''}`}
              >
                <div className="font-medium">{project.project_name}</div>
                <div>{getClientName(project.client_id)}</div>
                <div>{project.service_type}</div>
                <div>
                  {project.deadline
                    ? new Date(project.deadline).toLocaleDateString()
                    : 'N/A'}
                </div>
                <div>₹{project.value?.toLocaleString('en-IN')}</div>
                <div>{project.status}</div>
                <div>{project.payment_status}</div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(project)}>
                    <Edit className="w-4 h-4 text-blue-500" />
                  </button>
                  <button onClick={() => handleDelete(project.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* DIALOG */}
      <Dialog open={isDialogOpen} onOpenChange={(o) => !o && handleCancel()}>
        <DialogContent className="bg-app-card border border-app-border max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingProject ? 'Edit Project' : 'New Project'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">

            {/* CLIENT SEARCH */}
            <div>
              <Label>Client *</Label>
              <Input
                placeholder="Search client..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
              />

              {clientSearch && (
                <div className="mt-2 bg-app-surface border border-app-border rounded-md max-h-40 overflow-y-auto">
                  {clients
                    .filter(c =>
                      c.client_name.toLowerCase().includes(clientSearch.toLowerCase())
                    )
                    .map(client => (
                      <div
                        key={client.id}
                        className="px-3 py-2 hover:bg-app-muted cursor-pointer"
                        onClick={() => {
                          setFormData({ ...formData, client_id: client.id });
                          setClientSearch(client.client_name);
                        }}
                      >
                        {client.client_name}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div>
              <Label>Project Name *</Label>
              <Input
                required
                value={formData.project_name}
                onChange={(e) =>
                  setFormData({ ...formData, project_name: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Service Type *</Label>
              <Select
                value={formData.service_type}
                onValueChange={(val) =>
                  setFormData({ ...formData, service_type: val, status: '' })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map(type => (
                <SelectItem key={type.id} value={type.name}>
                  {type.name}
                </SelectItem>
                ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(val) =>
                  setFormData({ ...formData, status: val })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {(serviceStatusMap[formData.service_type] || []).map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Deadline</Label>
              <Input
                type="date"
                value={formData.deadline}
                onChange={(e) =>
                  setFormData({ ...formData, deadline: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Value (₹)</Label>
              <Input
                type="number"
                required
                value={formData.value}
                onChange={(e) =>
                  setFormData({ ...formData, value: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Assigned Person</Label>
              <Input
                value={formData.assigned_person}
                onChange={(e) =>
                  setFormData({ ...formData, assigned_person: e.target.value })
                }
              />
            </div>

          <div>
  <Label>Role</Label>
  <Select
    value={formData.assigned_role}
    onValueChange={(val) =>
      setFormData({ ...formData, assigned_role: val })
    }
  >
    <SelectTrigger>
      <SelectValue placeholder="Select role" />
    </SelectTrigger>

    <SelectContent>
      {roles.map(role => (
        <SelectItem key={role.id} value={role.name}>
          {role.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
</div>
            <div>
              <Label>Internal Notes</Label>
              <Textarea
                value={formData.internal_notes}
                onChange={(e) =>
                  setFormData({ ...formData, internal_notes: e.target.value })
                }
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">
                {editingProject ? 'Update' : 'Create'}
              </Button>
            </div>
{editingProject?.activity_log?.length > 0 && (
  <div className="mt-6">
    <Label>Activity Log</Label>
    <div className="mt-2 bg-app-surface border border-app-border rounded-md max-h-48 overflow-y-auto p-3 space-y-2">
      {editingProject.activity_log
        .slice()
        .reverse()
        .map((log, index) => (
          <div key={index} className="text-xs text-app-subtext">
            <div>{log.action}</div>
            <div className="text-[10px] opacity-60">
              {new Date(log.timestamp).toLocaleString()} • {log.user}
            </div>
          </div>
        ))}
    </div>
  </div>
)}
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Projects;